import React, { useEffect, useMemo, useState } from 'react';
import { Clock, Play } from 'lucide-react';
import ExerciseCard from './ExerciseCard';
import { estimateMinutes, formatDuration } from '../WorkoutPlayer/playerLogic';
import './planStudio.css';

// «Como o atleta vê»: o dia do plano desenhado como ele aparece no telemóvel do atleta. É uma PRÉ-VISUALIZAÇÃO: o
// botão de começar aparece com o aspeto que tem, mas não é um botão a sério (não faz nada) — está assinalado como tal.

const asPlayerShape = (ex) => ({
  trackingType: ex.trackingType || 'weight-reps',
  prescription: {
    setsTarget: Number(ex.setsTarget) || null,
    repsMin: Number(ex.repsMin) || null,
    repsMax: Number(ex.repsMax) || null,
    restSeconds: Number(ex.restSeconds) || null,
  },
});

export default function PlanPreview({ days = [], memberName = null, dayIndex = 0, onPickDay }) {
  const [index, setIndex] = useState(dayIndex);
  useEffect(() => { setIndex((cur) => (cur < days.length ? cur : 0)); }, [days.length]);
  useEffect(() => { setIndex(dayIndex); }, [dayIndex]);

  const day = days[Math.min(index, Math.max(0, days.length - 1))];
  const exercises = useMemo(() => day?.exercises || [], [day]);
  const minutes = useMemo(() => (exercises.length === 0 ? 0 : estimateMinutes(exercises.map(asPlayerShape))), [exercises]);
  const sets = useMemo(() => exercises.reduce((n, e) => n + (Number(e.setsTarget) || 0), 0), [exercises]);

  if (days.length === 0 || !day) {
    return <p className="ps-note" data-testid="preview-empty">Sem dias no plano para mostrar.</p>;
  }

  return (
    <div data-testid="plan-preview">
      <div className="ps-chip-row" style={{ marginBottom: 8 }}>
        <span className="ps-chip-label">Como o atleta vê</span>
        {days.map((d, i) => (
          <button
            key={i}
            type="button"
            className={`ps-chip ${i === index ? 'is-on' : ''}`}
            aria-pressed={i === index}
            onClick={() => { setIndex(i); onPickDay?.(i); }}
          >
            {d.dayName || `Dia ${i + 1}`}
          </button>
        ))}
      </div>

      <div className="ps-phone" aria-label="Pré-visualização no telemóvel do atleta" role="img">
        <div className="ps-phone-bar" aria-hidden="true" />
        <div className="ps-phone-body">
          <div className="ps-phone-head">
            <span className="ps-phone-title">{day.dayName || `Dia ${index + 1}`}</span>
            <span className="ps-phone-meta">
              {exercises.length === 1 ? '1 exercício' : `${exercises.length} exercícios`}
              {minutes > 0 ? ` · ${formatDuration(minutes * 60)}` : ''}
              {sets > 0 ? ` · ${sets} séries` : ''}
            </span>
            {memberName ? <span className="ps-phone-plan">{memberName}</span> : null}
          </div>

          {exercises.length === 0 ? (
            <p className="ps-note" style={{ padding: '0 12px' }}>Sem exercícios neste dia.</p>
          ) : (
            <div className="ps-phone-list">
              {exercises.map((ex, i) => (
                <ExerciseCard key={i} exercise={ex} index={i} editing={false} />
              ))}
            </div>
          )}

          <div className="ps-phone-cta" aria-hidden="true">
            <span className="ps-phone-btn"><Play size={14} aria-hidden="true" /> Começar treino</span>
            <span className="ps-phone-note"><Clock size={11} aria-hidden="true" /> pré-visualização — este botão é o do ecrã do atleta</span>
          </div>
        </div>
      </div>
    </div>
  );
}
